// Typ: FindInradiusEquilateralTask
// Temat: Okręgi w trójkącie → Okrąg wpisany w trójkąt równoboczny
// Wzór:  r = (a√3)/6

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// ===== generator =====
export function generateTask() {
  const a = Math.floor(Math.random() * 8) + 3; // bok 3..10
  return {
    type: "FindInradiusEquilateralTask",
    data: { a }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt równoboczny o boku
      <InlineMath math={`\\ a=${a}`} />.
      Oblicz promień okręgu wpisanego <InlineMath math={'r'} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'r'}
        placeholder="np. (4√3)/6, 2√3/3, 1.15"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a } = data;
  const expected = (a * Math.sqrt(3)) / 6;

  // 1) standardowy parse z RootFractionInput
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba dziesiętna (obsługa przecinka)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptuj w zapisie dziesiętnym do 0.01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a } = data;

  // r = (a√3)/6 -> skróć a/6
  const g = gcd(a, 6);
  const n = a / g;
  const d = 6 / g;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`r = \\dfrac{${n} \\sqrt{3}}{${d}}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a } = data;

  // przygotuj wersję skróconą
  const g = gcd(a, 6);
  const n = a / g;
  const d = 6 / g;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> W trójkącie równobocznym znamy wzór:</div>
      <BlockMath math={'r = \\dfrac{a\\sqrt{3}}{6}'} />

      <div><strong>Krok 2.</strong> Podstawiamy długość boku:</div>
      <BlockMath math={`r = \\dfrac{${a} \\sqrt{3}}{6}`} />

      <div><strong>Krok 3.</strong> Upraszczamy ułamek:</div>
      <BlockMath math={`r = \\dfrac{${n} \\sqrt{3}}{${d}}`} />
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "r")} />
    </div>
  );
}
